import { isFalsy, StatusPillWithInfo } from '@leapfinance/frontend-commons';
import {
  GBTypography,
  type TableColumn,
} from '@leapfinance/geebee-component-library';
import { format, parseISO } from 'date-fns';
import Link from 'next/link';

import { VASTypes } from '@/types/vas';

const getDateInMonthDayFormat = (date: string) => {
  try {
    if (!isFalsy(date)) {
      return format(parseISO(date), `MMMM d, yyyy`);
    } else {
      return `N/A`;
    }
  } catch (err) {
    return `N/A`;
  }
};

const getColumns = () => {
  const columns: TableColumn[] = [
    {
      field: `requestId`,
      hideTooltip: true,
      headerName: `ID`,
      width: 100,
      renderCell(params) {
        return (
          <Link href={`/profile/${params?.studentId}?key=VAS`} passHref>
            <a target={`_blank`}>
              <GBTypography
                variant="link2"
                className="text-primary-500 cursor-pointer"
              >
                {params?.requestId}
              </GBTypography>
            </a>
          </Link>
        );
      },
    },
    {
      field: `studentName`,
      headerName: `Student Name`,

      width: 200,
      renderCell(params) {
        return (
          <GBTypography variant="body4" className="text-grey-800">
            {params?.studentName}
          </GBTypography>
        );
      },
      flex: 1,
    },
    {
      field: `studentContactNumber`,
      headerName: `Contact Number`,
      width: 200,
      renderCell(params) {
        return (
          <GBTypography variant="body4" className="text-grey-800">
            {`${params?.countryCode}` + `${params?.contactNumber}`}
          </GBTypography>
        );
      },
      flex: 1,
    },
    {
      field: `serviceCategory`,
      headerName: `Request Type`,
      renderCell(params) {
        return (
          <GBTypography variant="body4" className="text-grey-800">
            {params?.serviceCategory}
          </GBTypography>
        );
      },
      flex: 1,
    },
    {
      field: `universityName`,
      headerName: `University`,
      renderCell(params) {
        return (
          <GBTypography variant="body4" className="text-grey-800">
            {params?.universityName}
          </GBTypography>
        );
      },
      flex: 1,
    },
    {
      field: `status`,
      renderCell(params) {
        return (
          <div className="flex items-center justify-center w-full ">
            <StatusPillWithInfo
              status={params?.status ?? ``}
              statusText={params?.statusLabel ?? ``}
            />
          </div>
        );
      },
      headerName: `Status`,
      flex: 0.7,
    },
    {
      field: `createdAt`,
      headerName: `Created On`,
      renderCell(params) {
        return (
          <GBTypography variant="body4" className="text-grey-800">
            {getDateInMonthDayFormat(params?.createdAt ?? ``)}
          </GBTypography>
        );
      },
      flex: 1,
    },
  ];

  return columns;
};

function getVasRequestFormTitle(type: VASTypes) {
  switch (type) {
    case `accomodation`:
      return `Request for Accommodation`;
    case `gic`:
      return `Request for GIC`;
    case `loan`:
      return `Raise Loan Request`;
    case `flywire`:
      return `Flywire Payment`;
    default:
      return ``;
  }
}

const NUMBER_OF_INTAKES = 6;

function formatFlywirePaymentLinkCreationDate(date: string) {
  try {
    // Format the parsed Date object into the desired format
    const formattedDate = format(new Date(date), `dd MMM yyyy 'at' HH:mm:ss`);
    return formattedDate;
  } catch (err) {
    return ``;
  }
}

function isStudentExisting(formValues: any) {
  return Object.values(formValues?.existingDuplicateStudent ?? {}).some(
    (item) => !isFalsy(item),
  );
}

const FLYWIRE_DETAILS_LOCAL_STORAGE_KEY = `flywireDetails`;

const getFlywireDetailsDataOnRedirect = (): {
  open?: boolean;
  paymentLink?: string;
} => {
  try {
    return JSON.parse(
      localStorage.getItem(FLYWIRE_DETAILS_LOCAL_STORAGE_KEY) ?? ``,
    );
  } catch (err) {
    return {};
  }
};

export {
  getColumns,
  getVasRequestFormTitle,
  NUMBER_OF_INTAKES,
  formatFlywirePaymentLinkCreationDate,
  isStudentExisting,
  FLYWIRE_DETAILS_LOCAL_STORAGE_KEY,
  getFlywireDetailsDataOnRedirect,
};
